import { Helmet } from "react-helmet";
import {
  Button,
  Dropdown,
  EmptyState,
  Flex,
  SimpleTable,
  Tag,
  Typography,
} from "djuno-design";
import useServiceType from "../../hooks/useServiceType";
import QuestionModal, {
  DeleteModal,
} from "../../components/modals/QuestionModal";

import RefreshIcon from "./../../assets/icons/arrow-path.svg?react";
import PlusIcon from "./../../assets/icons/plus.svg?react";
import DetailsIcon from "./../../assets/icons/bars-3-bottom-left.svg?react";
import ArchiveIcon from "./../../assets/icons/archive-box.svg?react";
import MoreIcon from "./../../assets/icons/more.svg?react";
import ArrowRightIcon from "./../../assets/icons/arrow-right.svg?react";
import StartIcon from "./../../assets/icons/play.svg?react";
import StopIcon from "./../../assets/icons/stop.svg?react";

import { useNavigate } from "react-router-dom";
import {
  AiTrainingsCreateUrl,
  AiTrainingsJobInformationUrl,
} from "../../utils/urls";
import HighlighterText from "../../components/general/HighlighterText";
import { useSearch } from "../../providers/SearchProvider";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useCallback, useEffect, useState } from "react";
import { AiJob, AiJobStatusState } from "../../types/ai";

import {
  selectAiJobs,
  selectAijobsLoading,
  selectAiJobsActionLoading,
  getAiJobsAsync,
  deleteAiJobAsync,
  stopAiJobAsync,
  relaunchAiJobAsync,
  AiJobsRefreshStatus,
} from "../../store/ai/jobs/jobsSlice";
import { formatTimestamp } from "../../utils/date";

const AiJobsPage = () => {
  const { serviceType } = useServiceType(
    import.meta.env.VITE_AI_TRAININGS_SERVICE_TYPE
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { value: searchValue } = useSearch();

  const [deletingJob, setDeletingJob] = useState<AiJob | null>(null);
  const [stoppingJob, setStoppingJob] = useState<AiJob | null>(null);
  const [relaunchingJob, setRelaunching] = useState<AiJob | null>(null);

  const jobs = useAppSelector(selectAiJobs);
  const jobsLoading = useAppSelector(selectAijobsLoading);

  const [filteredJobs, setFilteredJobs] = useState<AiJob[]>([]);

  const jobsActionLoading = useAppSelector(selectAiJobsActionLoading);

  useEffect(() => {
    dispatch(getAiJobsAsync({}));
  }, [dispatch]);

  useEffect(() => {
    const lookedUpFiles = jobs?.filter((job) =>
      job.spec.name.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredJobs(lookedUpFiles);
  }, [dispatch, searchValue, jobs]);

  //refresh the list
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const intervalTime =
      Number(import.meta.env.VITE_NODES_REFRESH_INTERVAL) || 20000;

    const shouldSetInterval = jobs.some((job) =>
      AiJobsRefreshStatus.includes(job.status.state || "")
    );

    if (shouldSetInterval) {
      interval = setInterval(() => {
        dispatch(getAiJobsAsync({ withoutLoading: true }));
      }, intervalTime);
    } else {
      if (interval) clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, jobs]);

  const handleStop = useCallback(
    (_job: AiJob) => {
      dispatch(stopAiJobAsync({ id: _job.id })).then((action) => {
        if (action.type === "ai-jobs/stop/fulfilled") {
          dispatch(getAiJobsAsync({ withoutLoading: true }));
          setStoppingJob(null);
        }
      });
    },
    [dispatch]
  );

  const handleRelaunch = useCallback(
    (_job: AiJob) => {
      dispatch(relaunchAiJobAsync({ job: _job })).then((action) => {
        if (action.type === "ai-jobs/relaunch/fulfilled") {
          dispatch(getAiJobsAsync({ withoutLoading: true }));
          setStoppingJob(null);
        }
      });
    },
    [dispatch]
  );

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | {serviceType?.ServiceName || ""}
        </title>
        <meta name="description" content="" />
      </Helmet>
      <div className="flex items-center justify-between h-20">
        <div className="px-6 items-center justify-between flex flex-1 transition duration-150">
          <Typography.Title level={5} className="!mb-0">
            {serviceType?.ServiceName}
          </Typography.Title>
        </div>
        {/* <div className="mr-6 w-96"></div> */}
      </div>
      <div className="mx-6">
        <div className="py-6">
          <div className="">
            <div className="flex items-center justify-between databases-header">
              <div className="items-center justify-between flex flex-1 transition duration-150">
                <Typography.Text className="font-medium text-lg"></Typography.Text>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => dispatch(getAiJobsAsync({}))}
                  className="refresh-button group"
                  tooltip={{ content: "Refresh" }}
                >
                  <RefreshIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
                </Button>
                <Button
                  uiType="primary"
                  onClick={() => navigate(AiTrainingsCreateUrl)}
                  className="create-database-button group"
                >
                  Launch a new Job
                  <PlusIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
                </Button>
              </div>
            </div>

            <div className="mt-5 w-full database-table">
              <SimpleTable
                loading={jobsLoading}
                containerClassName="min-h-[240px]"
              >
                <SimpleTable.Head>
                  <SimpleTable.Row>
                    {/* <SimpleTable.TH lable="Id" /> */}
                    <SimpleTable.TH lable="Name" />
                    <SimpleTable.TH lable="Region" />
                    <SimpleTable.TH lable="Docker Image" />
                    <SimpleTable.TH lable="Status" />
                    <SimpleTable.TH lable="Creation" />
                    <SimpleTable.TH lable="" />
                  </SimpleTable.Row>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {filteredJobs.map((job, i) => {
                    const { spec, id, status, createdAt } = job;
                    return (
                      <SimpleTable.Row
                        key={i}
                        onClick={() =>
                          navigate(AiTrainingsJobInformationUrl(id))
                        }
                      >
                        <SimpleTable.TD className="cursor-pointer">
                          <Flex>
                            <HighlighterText
                              searchValue={searchValue}
                              textToHighlight={spec.name}
                              className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate"
                            />
                          </Flex>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs md:!text-sm">
                            {spec.region}
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs">
                            {spec.image}
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <AiJobStatusTag state={status.state} />
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs">
                            {
                              formatTimestamp(createdAt, "D MMM YYYY HH:mm", {
                                isUTC: false,
                              }).datetime
                            }
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="w-32 gap-1">
                          <div className="h-full w-full inline-flex items-center justify-center gap-1">
                            <div className="w-8 flex justify-center items-center">
                              <Dropdown
                                anchor="bottom end"
                                itemsClassName="!w-48"
                                menu={[
                                  {
                                    key: "1",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <DetailsIcon className="w-4" />
                                        Details
                                      </div>
                                    ),
                                    onClick: (_, close) => {
                                      close();
                                      navigate(
                                        AiTrainingsJobInformationUrl(id)
                                      );
                                    },
                                    disabled: jobsLoading,
                                  },
                                  {
                                    key: "2",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <StartIcon className="w-4" />
                                        Relaunch
                                      </div>
                                    ),
                                    onClick: (_, close) => {
                                      close();
                                      setRelaunching(job);
                                      setStoppingJob(null);
                                    },
                                    disabled:
                                      jobsLoading ||
                                      jobsActionLoading ||
                                      status.state === "RESTARTING",
                                  },
                                  {
                                    key: "3",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <StopIcon className="w-4" />
                                        Stop
                                      </div>
                                    ),
                                    onClick: (_, close) => {
                                      close();
                                      setStoppingJob(job);
                                      setRelaunching(null);
                                    },
                                    disabled:
                                      jobsLoading ||
                                      jobsActionLoading ||
                                      job.status.state === "INTERRUPTED" ||
                                      job.status.state === "FINALIZING" ||
                                      job.status.state === "FAILED",
                                  },
                                  {
                                    type: "divider",
                                  },
                                  {
                                    key: "end",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <ArchiveIcon className="w-4" />
                                        Delete
                                      </div>
                                    ),
                                    danger: true,
                                    onClick: (_, close) => {
                                      close();
                                      setDeletingJob(job);
                                    },
                                    disabled:
                                      jobsLoading ||
                                      job.status.state === "RUNNING",
                                  },
                                ]}
                              >
                                <Button
                                  uiType="icon"
                                  uiSize="small"
                                  className="!px-2"
                                >
                                  <MoreIcon className="w-4 h-4" />
                                </Button>
                              </Dropdown>
                            </div>
                            <ArrowRightIcon className="w-4 h-4 dark:text-slate-100 text-slate-800 cursor-pointer" />
                          </div>
                        </SimpleTable.TD>
                      </SimpleTable.Row>
                    );
                  })}

                  {jobs.length === 0 && (
                    <SimpleTable.Row
                      withoutHoverStyle={true}
                      className="h-[200px]"
                    >
                      <SimpleTable.TD colSpan={6} className="!border-0">
                        <EmptyState text="You have not created any job yet" />
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  )}
                </SimpleTable.Body>
              </SimpleTable>
            </div>

            <DeleteModal
              title="Delete a AI job"
              isOpen={deletingJob !== null}
              onClose={() => setDeletingJob(null)}
              description=""
              confirmButtonText="Confirm"
              confirmButtonType="danger"
              confirmButtonClassName="w-full"
              loading={jobsActionLoading}
              onConfirm={async () => {
                if (!deletingJob) return;

                const action = await dispatch(
                  deleteAiJobAsync({ jobId: deletingJob.id })
                );

                if (action.type === "ai-jobs/delete/fulfilled") {
                  dispatch(getAiJobsAsync({}));
                  setDeletingJob(null);
                }
              }}
              confirmString={deletingJob?.spec.name}
            />

            <QuestionModal
              title="Stop the job"
              isOpen={stoppingJob !== null}
              onConfirm={() => stoppingJob && handleStop(stoppingJob)}
              onClose={() => {
                setStoppingJob(null);
              }}
              loading={jobsActionLoading}
              description={
                <Flex direction="col" className="gap-3">
                  <Typography.Text className="!text-sm mt-5">
                    You are about to stop the following job:
                  </Typography.Text>

                  <ul className="list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                    <li>
                      <Typography.Text className="!text-xs">
                        Job ID: {stoppingJob?.id}
                      </Typography.Text>
                    </li>
                    <li>
                      <Typography.Text className="!text-xs">
                        Region: {stoppingJob?.spec.region}
                      </Typography.Text>
                    </li>
                    <li>
                      <Typography.Text className="!text-xs">
                        Docker Image: {stoppingJob?.spec.image}
                      </Typography.Text>
                    </li>
                    <li>
                      <Typography.Text className="!text-xs">
                        Status:{" "}
                        <AiJobStatusTag state={stoppingJob?.status.state} />
                      </Typography.Text>
                    </li>
                    {stoppingJob && (
                      <li>
                        <Typography.Text className="!text-xs">
                          Creation:{" "}
                          {
                            formatTimestamp(
                              stoppingJob.createdAt,
                              "D MMM YYYY HH:mm:ss",
                              {
                                isUTC: false,
                              }
                            ).datetime
                          }
                        </Typography.Text>
                      </li>
                    )}
                  </ul>
                </Flex>
              }
              confirmButtonText="Confirm"
              confirmButtonType="primary"
              confirmButtonDisabled={stoppingJob === null}
            />

            <QuestionModal
              title="Relaunch the job"
              isOpen={relaunchingJob !== null}
              onConfirm={() => relaunchingJob && handleRelaunch(relaunchingJob)}
              onClose={() => {
                setRelaunching(null);
              }}
              loading={jobsActionLoading}
              description={
                <Flex direction="col" className="gap-3">
                  <Typography.Text className="!text-sm mt-5">
                    Are you sure you want to launch a new job with the same
                    settings?
                  </Typography.Text>
                  <ul className="list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                    <li>
                      <Typography.Text className="!text-xs">
                        Job ID: {relaunchingJob?.id}
                      </Typography.Text>
                    </li>
                    <li>
                      <Typography.Text className="!text-xs">
                        Region: {relaunchingJob?.spec.region}
                      </Typography.Text>
                    </li>
                  </ul>
                </Flex>
              }
              confirmButtonText="Confirm"
              confirmButtonType="primary"
              confirmButtonDisabled={relaunchingJob === null}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const AiJobStatusTag: React.FC<{
  state: AiJobStatusState | null | undefined;
}> = ({ state }) => {
  return (
    <Tag
      className="text-xs"
      color={
        state === "RUNNING" || state === "DONE"
          ? "success"
          : state === "ERROR" ||
            state === "FAILED" ||
            state === "SYNC_FAILED" ||
            state === "TIMEOUT"
          ? "error"
          : state === "RESTARTING" ||
            state === "FINALIZING" ||
            state === "INITIALIZING" ||
            state === "INTERRUPTING"
          ? "processing"
          : "warning"
      }
    >
      {state}
    </Tag>
  );
};
export default AiJobsPage;
